import React from 'react';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import type { ActivityReportResponse } from '@troia-v3/types';
interface ActivitySummaryCardProps {
  data: ActivityReportResponse;
}

/**
 * Card de resumo de atividades com breakdown por tipo e categoria
 *
 * Segue o padrão visual dos widgets da dashboard principal:
 * - Header compacto com WidgetHeaderIcon
 * - Tipografia consistente (text-sm font-semibold, text-xs)
 * - tabular-nums para valores numéricos
 */
export const ActivitySummaryCard: React.FC<ActivitySummaryCardProps> = ({ data }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Por Tipo */}
      <div
        className="rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="px-4 py-3 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <h3
                className="text-[15px] font-bold tracking-[-0.01em]"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Por Tipo
              </h3>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Distribuição por tipo de atividade
              </p>
            </div>
            <WidgetHeaderIcon icon={'activity'} />
          </div>
        </div>
        <div className="px-4 pb-4 space-y-3">
          {data.byType.length > 0 ? (
            data.byType.map((item) => {
              const completedPercent = item.count > 0 ? (item.completed / item.count) * 100 : 0;

              return (
                <div key={item.type}>
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="text-xs font-medium"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      {item.type}
                    </span>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}>
                        {item.count} total
                      </span>
                      <span style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}>
                        {item.completed} concluídas
                      </span>
                      {item.overdue > 0 && (
                        <span style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}>
                          {item.overdue} atrasadas
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'var(--theme-card-border)' }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${completedPercent}%`,
                        backgroundColor: 'var(--theme-success-color)',
                      }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <p
              className="text-xs text-center py-4"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Nenhuma atividade por tipo
            </p>
          )}
        </div>
      </div>

      {/* Por Categoria */}
      <div
        className="rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="px-4 py-3 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <h3
                className="text-[15px] font-bold tracking-[-0.01em]"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Por Categoria
              </h3>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Distribuição por categoria
              </p>
            </div>
            <WidgetHeaderIcon icon={'check-circle'} />
          </div>
        </div>
        <div className="px-4 pb-4 space-y-3">
          {data.byCategory.length > 0 ? (
            data.byCategory.map((item) => (
              <div
                key={item.category}
                className="flex items-center justify-between"
              >
                <span
                  className="text-xs font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {item.category}
                </span>
                <div className="flex items-center gap-3">
                  <span
                    className="text-[11px]"
                    style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {item.count}
                  </span>
                  <span
                    className="text-[11px] font-medium"
                    style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {item.completed} ok
                  </span>
                  {item.overdue > 0 && (
                    <span
                      className="text-[11px] font-medium"
                      style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {item.overdue} atrasadas
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p
              className="text-xs text-center py-4"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Nenhuma atividade por categoria
            </p>
          )}
        </div>
      </div>

      {/* Próximas Atividades */}
      <div
        className="rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="px-4 py-3 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <h3
                className="text-[15px] font-bold tracking-[-0.01em]"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Próximas a Vencer
              </h3>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Atividades com prazo próximo
              </p>
            </div>
            <WidgetHeaderIcon icon={'clock'} />
          </div>
        </div>
        <div className="px-4 pb-2">
          {data.upcomingDue.length > 0 ? (
            data.upcomingDue.slice(0, 5).map((activity, index) => (
              <div
                key={activity.id}
                className={`py-2.5 flex items-center justify-between ${index > 0 ? 'border-t' : ''}`}
                style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
              >
                <div>
                  <p
                    className="text-xs font-medium"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {activity.title}
                  </p>
                  <p
                    className="text-[10px]"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {activity.entityType}
                  </p>
                </div>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor:
                      activity.daysUntilDue <= 1
                        ? 'var(--theme-error-background)'
                        : activity.daysUntilDue <= 3
                        ? 'var(--theme-warning-background)'
                        : 'var(--theme-info-background)',
                    color:
                      activity.daysUntilDue <= 1
                        ? 'var(--theme-error-color)'
                        : activity.daysUntilDue <= 3
                        ? 'var(--theme-warning-color)'
                        : 'var(--theme-info-color)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {activity.daysUntilDue === 0
                    ? 'Hoje'
                    : activity.daysUntilDue === 1
                    ? 'Amanhã'
                    : `${activity.daysUntilDue}d`}
                </span>
              </div>
            ))
          ) : (
            <p
              className="text-xs text-center py-4"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Nenhuma atividade próxima
            </p>
          )}
        </div>
      </div>

      {/* Atividades Atrasadas */}
      <div
        className="rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="px-4 py-3 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h3
                  className="text-[15px] font-bold tracking-[-0.01em]"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Atrasadas
                </h3>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Atividades vencidas
                </p>
              </div>
              {data.overdueActivities.length > 0 && (
                <span
                  className="px-1.5 py-0.5 text-[10px] rounded-full font-medium"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    color: 'var(--theme-error-color)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {data.summary.overdue}
                </span>
              )}
            </div>
            <WidgetHeaderIcon icon={'alert-triangle'} />
          </div>
        </div>
        <div className="px-4 pb-2">
          {data.overdueActivities.length > 0 ? (
            data.overdueActivities.slice(0, 5).map((activity, index) => (
              <div
                key={activity.id}
                className={`py-2.5 flex items-center justify-between ${index > 0 ? 'border-t' : ''}`}
                style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
              >
                <div>
                  <p
                    className="text-xs font-medium"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {activity.title}
                  </p>
                  <p
                    className="text-[10px]"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {activity.actorName || activity.entityType}
                  </p>
                </div>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    color: 'var(--theme-error-color)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {activity.daysOverdue}d atrasada
                </span>
              </div>
            ))
          ) : (
            <p
              className="text-xs text-center py-4"
              style={{ color: 'var(--theme-success-color)' }}
            >
              Nenhuma atividade atrasada
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

ActivitySummaryCard.displayName = 'ActivitySummaryCard';
